import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageService } from '../services/storage.service';
import { MsalService } from '@azure/msal-angular';
import { catchError, from, Observable, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private readonly storageService: StorageService,
    private readonly msalService: MsalService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Check if request URL should be protected
    const isProtectedUrl = this.isProtectedResource(req.url);
    
    if (!isProtectedUrl) {
      return next.handle(req);
    }

    // Try to get MSAL token first
    const account = this.msalService.instance.getActiveAccount();
    
    if (account) {
      return from(this.getTokenSilently()).pipe(
        switchMap(token => {
          if (token) {
            const authReq = req.clone({
              headers: req.headers.set('Authorization', `Bearer ${token}`)
            });
            return next.handle(authReq);
          }
          return this.handleLegacyToken(req, next);
        }),
        catchError(() => {
          return this.handleLegacyToken(req, next);
        })
      );
    }

    // Fall back to legacy token
    return this.handleLegacyToken(req, next);
  }

  /**
   * Handle legacy OAuth token from storage
   */
  private handleLegacyToken(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const authToken = this.storageService.getItem('encodedAccessToken');

    if (authToken != null) {
      const authReq = req.clone({
        headers: req.headers.set('Authorization', 'Bearer ' + authToken)
      });
      return next.handle(authReq);
    } else {
      return next.handle(req.clone());
    }
  }

  /**
   * Check if the URL should be protected
   */
  private isProtectedResource(url: string): boolean {
    // Check if URL matches configured API URL
    if (environment.apiConfig?.uri) {
      return url.startsWith(environment.apiConfig.uri) || 
             url.includes('api.fordcredit.com') ||
             url.includes('api-dev.fordcredit.com') ||
             url.includes('api-qat.fordcredit.com');
    }
    return true;
  }

  /**
   * Get MSAL token silently
   */
  private async getTokenSilently(): Promise<string | null> {
    const account = this.msalService.instance.getActiveAccount();
    
    if (!account) {
      return null;
    }

    try {
      const response = await this.msalService.instance.acquireTokenSilent({
        scopes: environment.apiConfig.scopes,
        account: account
      });
      
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token silently:', error);
      return null;
    }
  }
}
